import {
  CalendarOutlined,
  CloseOutlined,
  DownOutlined,
  EllipsisOutlined,
} from '@ant-design/icons';
import { dehydrate, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { unstable_usePrompt } from 'react-router-dom';

import { CenterFlex, Divider, Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import FavoriteToggle from '@/components/Common/FavoriteToggle';
import LazyLoader from '@/components/Common/LazyLoader';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import Menu from '@/components/Menu/Menu';
import { MenuItemType } from '@/components/Menu/types';
import Modal from '@/components/Modal/Modal';
import Tooltip from '@/components/Tooltip/TooltipV2';
import Heading from '@/components/Typography/Heading';
import AMP_KEYS from '@/configs/amp-track-keys';
import { AnalysisState } from '@/features/Analysis/types';
import DashboardModal from '@/features/Dashboard/components/DashboardModal';
import DashboardRowList from '@/features/Dashboard/components/DashboardRowList';
import ErrorDisplay from '@/features/Dashboard/components/ErrorDisplay';
import ManageSubscribeModal from '@/features/Dashboard/components/ManageSubscribeModal';
import CreateSubscribeModal from '@/features/Dashboard/components/Modals/CreateSubscribeModal';
import CreateReportModal from '@/features/Report/components/CreateReportModal/CreateReportModal';
import {
  isStaticDashboardStorageSupported,
  saveStaticDashboardPayload,
} from '@/features/StaticDashboard2/lib/static-dashboard-storage';
import type { StaticDashboard2StoragePayload } from '@/features/StaticDashboard2/types';
import useStructuredFilterQuery from '@/features/StructuredFilter/hooks/useStructuredFilterQuery';
import StructuredFilter from '@/features/StructuredFilter/StructuredFilter';
import structuredFilterUtils from '@/features/StructuredFilter/utils/structuredFilter.utils';
import useUserRole from '@/hooks/domain/useUserRole';
import useWorkspaceId from '@/hooks/domain/useWorkspaceId';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useModalState from '@/hooks/pure/useModalState';
import service from '@/services/index';
import useDashboard2Store from '@/stores/Dashboard2Store';
import usePopupStore from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';
import useUIStore from '@/stores/pure/UIStore';
import useTagStore from '@/stores/TagStore';
import { useRecentDashboardHistory } from '@/stores/WorkspaceCacheStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { track } from '@/utils/track/track';
import { clipboardCopy, timeOut } from '@/utils/util';

import useDashboardActions from '../hooks/useDashboardActions';
import { useDashboardStarMutation } from '../hooks/useDashboardListQuery';
import AddChartDashboardMenu from './AddChartDashboardMenu/AddChartDashboardMenu';
import {
  DASHBOARD_DATE_FILTER_TYPE,
  DASHBOARD_DATE_FILTER_UNIT,
  DashboardDateFilter,
} from './DashboardDateFilter';
import { getDateRange } from './DashboardDateFilter/utils';
import ChartAnalysisSettingModal from './Modals/ChartAnalysisSettingModal';

type Props = {
  alias: string;
};

const Dashboard2DetailTemplate = (props: Props) => {
  const addToast = useToast();
  const renameDashboardModalState = useModalState();
  const chartAnalysisSettingModalState = useModalState();
  const { isEditor } = useUserRole();
  const { setPopup } = usePopupStore((state) => ({ setPopup: state.setPopup }));
  const wr = useWorkspaceRouter();
  const setGlobalLoading = useUIStore((state) => state.setGlobalLoading);
  const queryClient = useQueryClient();
  const createDashboardModalState = useModalState();
  const addSubscriptionModalState = useModalState();
  const manageSubscriptionModalState = useModalState();
  const createReportModalState = useModalState();
  const [isPdfExportSupported, setIsPdfExportSupported] = useState(false);
  const setCurrDashboard = useDashboard2Store(
    (state) => state.setCurrDashboard,
  );
  const globalDateFilter = useDashboard2Store(
    (state) => state.globalDateFilter,
  );
  const setGlobalDateFilter = useDashboard2Store(
    (state) => state.setGlobalDateFilter,
  );
  const setAnalysisMap = useDashboard2Store((state) => state.setAnalysisMap);
  const setAnalysisItem = useDashboard2Store((state) => state.setAnalysisItem);
  const analysisMap = useDashboard2Store((state) => state.analysisMap);
  const editingState = useDashboard2Store((state) => state.editingState);
  const setEditingState = useDashboard2Store((state) => state.setEditingState);
  const globalFilter = useDashboard2Store((state) => state.globalFilter);
  const setGlobalFilter = useDashboard2Store((state) => state.setGlobalFilter);
  const currWorkspaceTimezone = useWorkspaceStore(
    (state) => state.currWorkspace?.timezone,
  );
  const workspaceId = useWorkspaceId();
  const { setHistory, deleteHistory } = useRecentDashboardHistory();
  const { filterQueryFormats, onFilterSetChange, clearFilter } =
    useStructuredFilterQuery();

  const handleFilterChange = (formats: StructuredFilterQueryFormatT[]) => {
    onFilterSetChange(formats);
    const filter = structuredFilterUtils.buildStructuredFilter(formats);
    setGlobalFilter(filter || null);
  };

  const handleClearFilter = () => {
    clearFilter();
    setGlobalFilter(null);
  };

  const {
    data: dashboard,
    isLoading: isLoadingDashboard,
    isError: isErrorDashboard,
    refetch,
  } = useQuery({
    queryKey: ['dashboard2', props.alias],
    queryFn: async () => {
      if (props.alias) {
        return await service.dashboard2.fetchDashboard(props.alias);
      }
    },
  });

  useEffect(() => {
    if (dashboard) {
      setHistory(dashboard.alias);
      setCurrDashboard(dashboard);
      setAnalysisMap(dashboard.analyses || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard]);

  useEffect(() => {
    let cancelled = false;

    const checkPdfExportSupport = async () => {
      const isSupported = await isStaticDashboardStorageSupported();
      if (cancelled) return;
      setIsPdfExportSupported(isSupported);
    };

    void checkPdfExportSupport();

    return () => {
      cancelled = true;
    };
  }, []);

  const { mutate: updateDashboardStarred } = useDashboardStarMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard2', props.alias] });
    },
  });

  const addChartBtnRef = useRef<HTMLButtonElement>(null);
  const { createDashboard } = useDashboardActions();

  const onClickCreateDashboard = useCallback(
    async (dashboardName: string) => {
      track(AMP_KEYS.DASHBOARD_DETAIL_CREATE_DASHBOARD_CLICK);
      createDashboardModalState.close();
      await timeOut(200);
      createDashboard(dashboardName);
    },
    [createDashboard, createDashboardModalState],
  );

  const onClickShareDashboard = useCallback(() => {
    track(AMP_KEYS.DASHBOARD_DETAIL_SHARE_CLICK);
    clipboardCopy(window.location.href);

    addToast('Dashboard link Copied', 'success');
  }, [addToast]);

  const onClickAddChart = useCallback(() => {
    track(AMP_KEYS.DASHBOARD_DETAIL_ADD_CHART_CLICK);
    wr.push(`/charts/create`);
  }, [wr]);

  const onClickDuplicateDashboard = useCallback(async () => {
    if (!dashboard) return;
    track(AMP_KEYS.DASHBOARD_DETAIL_DUPLICATE_DASHBOARD_CLICK);

    try {
      const duplicatedDashboard = await service.dashboard2.duplicateDashboard(
        dashboard?.alias,
      );

      // move to the duplicated dashboard
      wr.push(`/dashboard2/${duplicatedDashboard.alias}`);

      // refetch dashboard list
      queryClient.invalidateQueries({ queryKey: ['dashboard2-list'] });

      addToast('Dashboard duplicated', 'success');
    } catch (e: unknown) {
      addToast('Dashboard duplication failed', 'fail');
    }
  }, [addToast, dashboard, queryClient, wr]);

  const onClickDeleteDashboard = useCallback(() => {
    const dashboardAlias = dashboard?.alias;
    if (!dashboardAlias) return;

    track(AMP_KEYS.DASHBOARD_DETAIL_DELETE_DASHBOARD_CLICK);
    setPopup({
      popupTheme: 'negative',
      title: 'Delete dashboard',
      message: 'Are you sure you want to delete this dashboard?',
      type: 'confirm',
      onConfirm: async () => {
        await service.dashboard2.deleteDashboard(dashboardAlias);
        deleteHistory([dashboardAlias]);
        wr.push('/dashboard2');
      },
    });
  }, [dashboard, deleteHistory, setPopup, wr]);

  const onClickRenameDashboard = useCallback(() => {
    track(AMP_KEYS.DASHBOARD_DETAIL_RENAME_DASHBOARD_CLICK);
    renameDashboardModalState.open();
  }, [renameDashboardModalState]);

  const onClickExportToPdf = useCallback(async () => {
    if (!dashboard) return;

    track(AMP_KEYS.DASHBOARD_DETAIL_EXPORT_TO_PDF_CLICK);

    // 원하는 창 크기 설정
    const width = 1920; // 원하는 너비
    const height = 1080; // 원하는 높이

    // 창 위치를 화면 중앙으로 계산
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    // 창 옵션 설정
    const windowFeatures = [
      'popup', // 팝업 창으로 열기
      `width=${width}`, // 너비
      `height=${height}`, // 높이
      `left=${left}`, // 왼쪽 위치
      `top=${top}`, // 위쪽 위치
      'resizable=no', // 크기 조절 가능
      'scrollbars=no', // 스크롤바 표시
      'status=no', // 상태바 숨김
      'menubar=no', // 메뉴바 숨김
      'toolbar=no', // 툴바 숨김
    ].join(',');

    setGlobalLoading(true);
    try {
      const serializedGlobalDateFilter = globalDateFilter
        ? {
            ...globalDateFilter,
            dateRange: globalDateFilter.dateRange
              ? ([
                  globalDateFilter.dateRange[0].toJSON(),
                  globalDateFilter.dateRange[1].toJSON(),
                ] as [string, string])
              : null,
          }
        : null;
      const tagMap = useTagStore.getState().tagMap;
      const queryState = dehydrate(queryClient, {
        shouldDehydrateQuery: (query) => {
          const [firstKey, secondKey] = query.queryKey;
          if (query.state.status !== 'success') return false;
          if (firstKey === 'chart-table') return true;
          return firstKey === 'dashboard' && secondKey === 'feedback-gallery';
        },
      });
      const storagePayload: StaticDashboard2StoragePayload = {
        dashboard,
        queryState,
        workspaceId,
        tagMap,
        timezone: currWorkspaceTimezone ?? null,
        globalFilter,
        globalDateFilter: serializedGlobalDateFilter,
      };

      await saveStaticDashboardPayload(storagePayload);
    } finally {
      setGlobalLoading(false);
    }

    const printWindow = window.open(
      `${window.location.origin}/static/dashboard2`,
      'Export dashboard',
      windowFeatures,
    );

    if (!printWindow) {
      alert('Popup is blocked. Please allow popups.');
      return;
    }

    // 3. 페이지 로드 완료 후 프린트 실행
    printWindow.onload = () => {
      // 4. 특정 요소 렌더링 확인
      const checkRendering = () => {
        const targetElement =
          printWindow.document.querySelector('#static-root');
        if (!targetElement) {
          setTimeout(checkRendering, 100);
          return;
        }

        const dimensions = {
          width,
          height: printWindow.document.documentElement.scrollHeight,
        };

        const styleSheet = printWindow.document.createElement('style');
        styleSheet.textContent = `
          @page {
            /* 페이지 크기 지정 */
            size: ${dimensions.width}px 2200px;
          }
        `;
        printWindow.document.head.appendChild(styleSheet);

        // 7. 프린트 다이얼로그가 닫힐 때 창 닫기
        printWindow.onafterprint = () => {
          printWindow.close();
        };

        // 6. 프린트 실행
        setTimeout(() => {
          printWindow.print();
        }, 3000);
      };

      checkRendering();
    };
  }, [
    currWorkspaceTimezone,
    dashboard,
    globalDateFilter,
    globalFilter,
    props.alias,
    queryClient,
    setGlobalLoading,
    workspaceId,
  ]);

  const dashboardEllipsisMenuOptions = useMemo(() => {
    return [
      {
        label: 'Rename',
        onClick: onClickRenameDashboard,
      },
      {
        label: 'Duplicate',
        onClick: onClickDuplicateDashboard,
      },
      {
        label: 'Delete',
        onClick: onClickDeleteDashboard,
      },
      {
        label: 'divider',
        type: MenuItemType.DIVIDER,
      },
      {
        label: 'Smart brief',
        subMenus: [
          {
            label: 'Setting',
            onClick: () => chartAnalysisSettingModalState.open(),
          },
          {
            label: 'Refresh all',
            onClick: () => {
              if (!dashboard) return;
              setPopup({
                popupTheme: 'negative',
                title: 'Are you sure you want to refresh all charts?',
                message: 'Previous answers will be deleted.',
                type: 'confirm',
                onConfirm: async () => {
                  const res = await service.analysis.syncAnalysisAll(
                    dashboard.alias,
                  );
                  res.fail_ids.forEach((id) => {
                    // TODO: some error handling;
                  });
                  res.success_ids.forEach((id) => {
                    const analysis = analysisMap[id];
                    if (analysis) {
                      const newAnalysis = {
                        ...analysis,
                        state: AnalysisState.IN_PROGRESS,
                      };
                      setAnalysisItem(id, newAnalysis);
                    }
                  });
                },
              });
            },
          },
        ],
      },
    ];
  }, [
    analysisMap,
    chartAnalysisSettingModalState,
    dashboard,
    onClickDeleteDashboard,
    onClickDuplicateDashboard,
    onClickRenameDashboard,
    setAnalysisItem,
    setPopup,
  ]);

  const dashboardShareMenuOptions = useMemo(() => {
    const options = [
      {
        label: 'Copy link',
        onClick: onClickShareDashboard,
      },
      {
        label: 'Export as report',
        onClick: () => {
          track(AMP_KEYS.DASHBOARD_EXPORT_AS_REPORT_CLICK);
          createReportModalState.open();
        },
        disabled: !isEditor,
      },

      {
        label: 'divider',
        type: MenuItemType.DIVIDER,
      },
      {
        label: 'Subscribe',
        subMenuWidth: 200,
        subMenus: [
          {
            label: 'Add subscription',
            onClick() {
              addSubscriptionModalState.open();
            },
          },
          {
            label: 'Manage subscription',
            onClick() {
              manageSubscriptionModalState.open();
            },
          },
        ],
      },
    ];

    if (isPdfExportSupported) {
      options.splice(2, 0, {
        label: 'Export to PDF',
        onClick: onClickExportToPdf,
      });
    }

    return options;
  }, [
    addSubscriptionModalState,
    createReportModalState,
    isPdfExportSupported,
    isEditor,
    manageSubscriptionModalState,
    onClickExportToPdf,
    onClickShareDashboard,
  ]);

  // Page-level prompt handling for any editing state
  unstable_usePrompt({
    when: editingState,
    message: 'Changes you made may not be saved.',
  });

  useEffect(() => {
    return () => {
      setEditingState(false);
    };
  }, []);

  if (isErrorDashboard) throw new Error('Dashboard not found');

  return (
    <Transition
      status={!isLoadingDashboard}
      fallback={
        <LazyLoader
          show={isLoadingDashboard}
          delay={500}
          loader={
            <CenterFlex height="100%">
              <Spinner />
            </CenterFlex>
          }
        />
      }
      style={{
        height: '100%',
      }}
    >
      {dashboard && (
        <VFlex height="100%">
          <VFlex j="space-between" padding="32px 40px" g={16}>
            <Flex j="space-between" f={1}>
              <Button
                leftIcon={<ChevronLeft />}
                sizeType="m"
                variant="ghost_secondary"
                edgeAlign="right"
                onClick={() => wr.push('/dashboard2/list')}
              >
                Dashboards
              </Button>
            </Flex>
            <Flex j="space-between" f={1}>
              <Flex a="center" f={1} g={16}>
                <Tooltip tooltipNode={dashboard.title}>
                  <Heading
                    type="h2"
                    style={{
                      maxWidth: 600,
                    }}
                    multilineEllipsis={1}
                    onClick={async () => {
                      await timeOut(10);
                    }}
                  >
                    {dashboard.title}
                  </Heading>
                </Tooltip>

                <FavoriteToggle
                  value={!!dashboard.starred_at}
                  onChange={(value) => {
                    updateDashboardStarred({
                      dashboardAlias: dashboard.alias,
                      starred: value,
                    });
                  }}
                  tooltip={
                    dashboard.starred_at ? 'Remove from home' : 'Add to home'
                  }
                  outlined
                />
              </Flex>
              <Flex g={8} a="center">
                <Menu
                  triggerNode={
                    <Button
                      variant="primary"
                      sizeType="m"
                      rightIcon={<DownOutlined />}
                    >
                      Share
                    </Button>
                  }
                  options={dashboardShareMenuOptions}
                />

                {isEditor && (
                  <Button
                    onClick={() => createDashboardModalState.open()}
                    variant="outlined"
                    sizeType="m"
                  >
                    Create dashboard
                  </Button>
                )}

                <AddChartDashboardMenu
                  currentCharts={dashboard.charts?.map((chart) => chart.alias)}
                  anchor={
                    <Button
                      variant="outlined"
                      sizeType="m"
                      onClick={onClickAddChart}
                      ref={addChartBtnRef}
                      leftIcon={<Plus />}
                    >
                      Add
                    </Button>
                  }
                />

                <Menu
                  modalWidth={180}
                  triggerNode={
                    <IconButton icon={<EllipsisOutlined />} sizeType="m" />
                  }
                  options={dashboardEllipsisMenuOptions}
                />
              </Flex>
            </Flex>
          </VFlex>

          <Divider />

          <Flex j="space-between" a="flex-start" padding="16px 40px 16px">
            <StructuredFilter
              values={filterQueryFormats}
              onChange={handleFilterChange}
              clearFilter={handleClearFilter}
            />

            {globalDateFilter ? (
              <Flex>
                <DashboardDateFilter
                  value={globalDateFilter}
                  onChange={setGlobalDateFilter}
                />
                <IconButton
                  icon={<CloseOutlined />}
                  outlined={false}
                  sizeType="m"
                  onClick={() => setGlobalDateFilter(null)}
                  tooltip="Remove date filter"
                />
              </Flex>
            ) : (
              <Button
                leftIcon={<CalendarOutlined />}
                sizeType="m"
                variant="outlined"
                tooltip={
                  <p>
                    This filter temporarily affects all charts on this
                    dashboard.
                    <br />
                    It does not yet affect <strong>Export PDF</strong> and{' '}
                    <strong>Export Report,</strong>
                    <br />
                    but support will be added soon.
                  </p>
                }
                onClick={() =>
                  setGlobalDateFilter({
                    unit: DASHBOARD_DATE_FILTER_UNIT.DAY,
                    type: DASHBOARD_DATE_FILTER_TYPE.LAST_7_DAYS,
                    dateRange: getDateRange(
                      DASHBOARD_DATE_FILTER_TYPE.LAST_7_DAYS,
                    ),
                  })
                }
              >
                Override date filter
              </Button>
            )}
          </Flex>

          <ErrorBoundary fallback={<ErrorDisplay height="300px" />}>
            <DashboardRowList dashboard={dashboard} />
          </ErrorBoundary>

          <Modal
            status={addSubscriptionModalState.status}
            closeModal={addSubscriptionModalState.close}
          >
            <CreateSubscribeModal
              dashboardAlias={props.alias}
              dashboardName={dashboard.title}
            />
          </Modal>

          <Modal
            status={manageSubscriptionModalState.status}
            closeModal={manageSubscriptionModalState.close}
          >
            <ManageSubscribeModal dashboardAlias={props.alias} />
          </Modal>

          <Modal
            status={createDashboardModalState.status}
            closeModal={createDashboardModalState.close}
          >
            <DashboardModal onConfirm={onClickCreateDashboard} />
          </Modal>

          <Modal
            status={renameDashboardModalState.status}
            closeModal={renameDashboardModalState.close}
          >
            <DashboardModal
              value={dashboard.title}
              onConfirm={async (dashboardName) => {
                await service.dashboard2.changeDashboardTitle(
                  dashboard.alias,
                  dashboardName,
                );
                renameDashboardModalState.close();
                refetch();
              }}
              title="Rename dashboard"
              label="Dashboard name"
              buttonLabel="Rename"
            />
          </Modal>

          <Modal
            status={chartAnalysisSettingModalState.status}
            closeModal={chartAnalysisSettingModalState.close}
          >
            <ChartAnalysisSettingModal />
          </Modal>

          <Modal
            status={createReportModalState.status}
            closeModal={createReportModalState.close}
          >
            <CreateReportModal dashboard_alias={dashboard.alias} />
          </Modal>
        </VFlex>
      )}
    </Transition>
  );
};

export default Dashboard2DetailTemplate;
